import { useMemo, useState } from 'react';
import { App as AntApp, Button } from 'antd';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { InfoDot, MoneyInput } from './MortgagePage';
import { num, valuationFee } from '../lib/loanCalc';

// Valuation — bank-valuation fee per the LPPEH scale (min RM400) + disbursement,
// with 8% SST applied on the fee + disbursement (matches the reference tool).

const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SST_RATE = 0.08;

export default function ValuationPage() {
  const { message } = AntApp.useApp();
  const [price, setPrice] = useState('');
  const [disb, setDisb] = useState('50');
  const [show, setShow] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const r = useMemo(() => {
    const fee = valuationFee(num(price));
    const d = num(disb);
    const sst = (fee + d) * SST_RATE;
    return { fee, disb: d, sst, total: fee + d + sst };
  }, [price, disb]);

  const share = async () => {
    const text = [
      'Valuation',
      `Property Price: ${fmt2(num(price))}`,
      '',
      `Valuation Fee: ${fmt2(r.fee)}`,
      `Disbursement: ${fmt2(r.disb)}`,
      `SST (8%): ${fmt2(r.sst)}`,
      `Total: RM${fmt2(r.total)}`,
    ].join('\n');
    try {
      if (navigator.share) { await navigator.share({ text }); return; }
      await navigator.clipboard.writeText(text);
      message.success('Result copied to clipboard');
    } catch (_) { /* cancelled */ }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Valuation" right={<InfoDot onClick={() => setInfoOpen(true)} />} />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Property Price">
          <MoneyInput value={price} onChange={setPrice} placeholder="0" />
        </Field>
        <Field label="Disbursement">
          <MoneyInput value={disb} onChange={setDisb} placeholder="50" />
        </Field>

        {show && num(price) > 0 && (
          <>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px 4px', fontSize: 12.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--primary)' }}>SUMMARY</div>
              <Row label="Valuation Fee" value={fmt2(r.fee)} />
              <Row label="Disbursement" value={fmt2(r.disb)} />
              <Row label="SST (8%)" value={fmt2(r.sst)} />
              <Row label="Total (RM)" value={fmt2(r.total)} strong />
            </div>
            <Button block size="large" type="primary" onClick={share}>Share Result</Button>
          </>
        )}
      </div>

      <InfoNotes
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="Valuation"
        pages={[
          { body: 'Valuation fee is calculated based on lppeh.gov.my (The Board of Valuers, Appraisers, Estate Agents and Property Managers).' },
          { title: 'Scale', body: 'LPPEH scale: 0.25% on the first RM100k, 0.2% on the next RM2m, 0.167% on the next RM7m, 0.125% on the next RM15m, 0.1% on the next RM50m — minimum RM400.\n\nSST (8%) applies on the valuation fee + disbursement.' },
        ]}
      />

      <div style={{ display: 'flex', gap: 12, padding: '12px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none' }}>
        <Button block size="large" onClick={() => { setPrice(''); setDisb('50'); setShow(false); }}>Reset</Button>
        <Button block size="large" type="primary" onClick={() => (num(price) > 0 ? setShow(true) : message.warning('Enter the property price'))}>Calculate</Button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>{label}</label>
    {children}
  </div>
);

const Row = ({ label, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '14px 16px', borderTop: '1px solid var(--border)', fontSize: 13, background: strong ? 'var(--chip)' : undefined }}>
    <span style={{ fontWeight: strong ? 600 : 400, color: strong ? 'var(--primary)' : 'var(--text)' }}>{label}</span>
    <b style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: strong ? 'var(--primary)' : 'var(--text)' }}>{value}</b>
  </div>
);
